import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { type Range, getMarkRange } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";
import { type Editor, useEditorState } from "@tiptap/react";
import React from "react";
import Button from "../../common/Button/Button";
import FloatingPanel from "../../common/FloatingPanel/FloatingPanel";
import Icon from "../../common/Icon/Icon";
import Popover from "../../common/Popover/Popover";
import TextInput from "../TextInput/TextInput";
import styles from "./RichTextEditor.module.scss";
import { preserveSelection, useRichTextEditor } from "./context";

/** A link in the document, or the selection a new link would go on. */
interface LinkTarget {
  range: Range;
  href: string;
  /** The text in `range`, or null when it holds something other than text (e.g. an attribute chip), so it can't be edited as text. */
  text: string | null;
}

/** The link at `pos` (default: the selection), or the selection itself when there is no link there. */
const linkTarget = (editor: Editor, pos?: number): LinkTarget | null => {
  const { doc, selection, schema } = editor.state;
  const linkType = schema.marks.link;
  if (!linkType) return null;
  const at = pos ?? selection.from;
  const markRange = getMarkRange(doc.resolve(at), linkType);
  if (!markRange && pos !== undefined) return null;
  const range = markRange ?? { from: selection.from, to: selection.to };
  let textOnly = true;
  doc.nodesBetween(range.from, range.to, (node) => {
    if (node.isInline && !node.isText) textOnly = false;
  });
  const mark = markRange ? doc.nodeAt(markRange.from)?.marks.find((each) => each.type === linkType) : undefined;
  return {
    range,
    href: (mark?.attrs.href as string | undefined) ?? "",
    text: textOnly ? doc.textBetween(range.from, range.to) : null,
  };
};

/** Adds `https://` to a bare domain. Leaves schemes, relative paths, anchors and `{{ }}` templates alone. */
export const normalizeHref = (href: string): string => {
  const url = href.trim();
  if (!url || /^[a-z][\w+.-]*:/i.test(url) || /^[/#?]/.test(url) || url.startsWith("{{")) return url;
  return `https://${url}`;
};

/**
 * Links `range` to `href`, replacing its text with `text` when that is given and
 * different. An empty `href` removes the link. With an empty range and some
 * text, inserts the text as a new link.
 */
export const saveLink = (editor: Editor, range: Range, href: string, text?: string | null): boolean => {
  const url = normalizeHref(href);
  const linkType = editor.schema.marks.link;
  if (!linkType) return false;
  return editor
    .chain()
    .focus()
    .command(({ tr }) => {
      let { from, to } = range;
      if (!url) {
        tr.removeMark(from, to, linkType);
        return true;
      }
      if (text && text !== tr.doc.textBetween(from, to)) {
        const $from = tr.doc.resolve(from);
        const marks = (from === to ? (tr.storedMarks ?? $from.marks()) : $from.marksAcross(tr.doc.resolve(to))) ?? [];
        tr.replaceWith(
          from,
          to,
          editor.schema.text(
            text,
            marks.filter((mark) => mark.type !== linkType),
          ),
        );
        to = from + text.length;
      }
      if (from === to) return false;
      tr.removeMark(from, to, linkType).addMark(from, to, linkType.create({ href: url }));
      tr.setSelection(TextSelection.create(tr.doc, to));
      return true;
    })
    .run();
};

interface LinkFormProps {
  href: string;
  /** The link text, or null to hide the text field. */
  text: string | null;
  onSave: (href: string, text: string | null) => void;
  onRemove?: () => void;
}

const LinkForm = ({ href: initialHref, text: initialText, onSave, onRemove }: LinkFormProps) => {
  const [href, setHref] = React.useState(initialHref);
  const [text, setText] = React.useState(initialText ?? "");
  return (
    <form
      className={styles.linkForm}
      onSubmit={(event) => {
        event.preventDefault();
        onSave(href, initialText === null ? null : text);
      }}
    >
      <TextInput
        value={href}
        onValueChange={setHref}
        placeholder="https://"
        aria-label="Link URL"
        fullWidth
        autoFocus
      />
      {initialText === null ? null : (
        <TextInput
          value={text}
          onValueChange={setText}
          placeholder="Link text"
          aria-label="Link text"
          fullWidth
        />
      )}
      <div className={styles.linkActions}>
        {onRemove ? (
          <Button
            label="Remove"
            variant="ghost"
            size="sm"
            intent="destructive"
            onClick={onRemove}
          />
        ) : null}
        <Button
          label="Save"
          intent="primary"
          size="sm"
          type="submit"
        />
      </div>
    </form>
  );
};

/** Toolbar link control: a small popover to set, change, or remove the link on the selection. */
export const RichTextEditorLink = () => {
  const { editor, disabled } = useRichTextEditor();
  const [target, setTarget] = React.useState<LinkTarget | null>(null);
  const active = useEditorState({ editor, selector: ({ editor: current }) => current?.isActive("link") ?? false });
  const close = () => setTarget(null);

  return (
    <Popover
      visible={target !== null}
      onVisibleChange={(visible) => (visible ? null : close())}
      mode="click"
      interactable
      placement="bottom-start"
      offset={6}
    >
      {(popover) => (
        <>
          <Popover.Reference
            asChild
            trigger={false}
          >
            <Button
              icon
              size="sm"
              label="Link"
              selected={Boolean(active) || target !== null}
              disabled={!editor || disabled}
              onMouseDown={preserveSelection}
              onClick={() => (target || !editor ? close() : setTarget(linkTarget(editor)))}
            >
              <Icon name="link" />
            </Button>
          </Popover.Reference>
          <Popover.Content
            asChild
            handleVisibility={false}
          >
            <FloatingPanel
              open={popover.visible}
              placement={popover.placement}
              unmountWhenHidden
              className={styles.linkPanel}
            >
              {target && editor ? (
                <LinkForm
                  href={target.href}
                  text={target.text}
                  onSave={(href, text) => {
                    saveLink(editor, target.range, href, text);
                    close();
                  }}
                  onRemove={
                    target.href
                      ? () => {
                          saveLink(editor, target.range, "");
                          close();
                        }
                      : undefined
                  }
                />
              ) : null}
            </FloatingPanel>
          </Popover.Content>
        </>
      )}
    </Popover>
  );
};

const OPEN_DELAY = 300;
const CLOSE_DELAY = 200;

const hostOf = (href: string) => {
  try {
    return new URL(href).hostname.replace(/^www\./, "") || href;
  } catch {
    return href;
  }
};

/**
 * Hover card for links in the content: shows where the link goes, with Open,
 * Copy, Edit and Remove. Edit swaps the card for the link form (URL and text)
 * in place. Returns the handlers to put on the content element, and the card.
 */
export const useLinkHoverCard = (): {
  contentProps: Pick<React.HTMLAttributes<HTMLElement>, "onMouseOver" | "onMouseOut">;
  card: React.ReactNode;
} => {
  const { editor, disabled } = useRichTextEditor();
  const [anchor, setAnchor] = React.useState<HTMLAnchorElement | null>(null);
  const [editing, setEditing] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const openTimer = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  const clearTimers = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  };
  const close = React.useCallback(() => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    setAnchor(null);
    setEditing(false);
    setCopied(false);
  }, []);
  const scheduleClose = () => {
    clearTimeout(openTimer.current);
    if (editing) return;
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(close, CLOSE_DELAY);
  };

  React.useEffect(() => clearTimers, []);

  // An edit can re-render the link as a new element; the card has nothing left to point at.
  React.useEffect(() => {
    if (!editor || !anchor) return;
    const onUpdate = () => {
      if (!anchor.isConnected) close();
    };
    editor.on("update", onUpdate);
    return () => {
      editor.off("update", onUpdate);
    };
  }, [editor, anchor, close]);

  const { refs, floatingStyles, context, placement } = useFloating({
    open: anchor !== null,
    onOpenChange: (open) => (open ? null : close()),
    elements: { reference: anchor },
    placement: "bottom-start",
    strategy: "fixed",
    // Position with top/left: FloatingPanel's open animation sets `transform`, which would
    // override a translate and leave the card at the top left of the screen.
    transform: false,
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);

  const contentProps = {
    onMouseOver: (event: React.MouseEvent<HTMLElement>) => {
      const link = (event.target as HTMLElement).closest?.<HTMLAnchorElement>("a[href]");
      if (!link || disabled || editing) return;
      clearTimeout(closeTimer.current);
      if (link === anchor) return;
      clearTimeout(openTimer.current);
      // Already showing one card: move straight to the next link.
      if (anchor) setAnchor(link);
      else openTimer.current = setTimeout(() => setAnchor(link), OPEN_DELAY);
    },
    onMouseOut: (event: React.MouseEvent<HTMLElement>) => {
      const to = event.relatedTarget as HTMLElement | null;
      if (to?.closest?.("a[href]") && event.currentTarget.contains(to)) return;
      scheduleClose();
    },
  };

  const target = anchor && editor ? linkTarget(editor, editor.view.posAtDOM(anchor, 0)) : null;
  const editable = Boolean(editor?.isEditable) && !disabled;
  const href = target?.href ?? anchor?.getAttribute("href") ?? "";
  const openable = Boolean(href) && !href.includes("{{");

  const card =
    anchor && editor ? (
      <FloatingPortal>
        <FloatingPanel
          ref={refs.setFloating}
          open
          placement={placement}
          style={floatingStyles}
          className={editing ? styles.linkPanel : styles.linkCard}
          onMouseEnter={() => clearTimeout(closeTimer.current)}
          onMouseLeave={scheduleClose}
          {...getFloatingProps()}
        >
          {editing && target ? (
            <LinkForm
              href={target.href}
              text={target.text}
              onSave={(nextHref, text) => {
                saveLink(editor, target.range, nextHref, text);
                close();
              }}
              onRemove={() => {
                saveLink(editor, target.range, "");
                close();
              }}
            />
          ) : (
            <>
              <div className={styles.linkCardBody}>
                <Icon
                  name="globe"
                  className={styles.linkCardIcon}
                  aria-hidden
                />
                <div className={styles.linkCardText}>
                  <div className={styles.linkCardHost}>{hostOf(href)}</div>
                  <div
                    className={styles.linkCardHref}
                    title={href}
                  >
                    {href}
                  </div>
                </div>
              </div>
              <div className={styles.linkCardActions}>
                <Button
                  icon
                  size="sm"
                  label="Open link"
                  disabled={!openable}
                  onMouseDown={preserveSelection}
                  onClick={() => window.open(href, "_blank", "noopener,noreferrer")}
                >
                  <Icon name="open-in-new" />
                </Button>
                <Button
                  icon
                  size="sm"
                  label={copied ? "Copied" : "Copy link"}
                  onMouseDown={preserveSelection}
                  onClick={() => {
                    void navigator.clipboard?.writeText(href).then(() => setCopied(true));
                  }}
                >
                  <Icon name={copied ? "check" : "copy"} />
                </Button>
                {editable && target ? (
                  <>
                    <Button
                      icon
                      size="sm"
                      label="Edit link"
                      onMouseDown={preserveSelection}
                      onClick={() => setEditing(true)}
                    >
                      <Icon name="edit" />
                    </Button>
                    <Button
                      icon
                      size="sm"
                      label="Remove link"
                      onMouseDown={preserveSelection}
                      onClick={() => {
                        saveLink(editor, target.range, "");
                        close();
                      }}
                    >
                      <Icon name="unlink" />
                    </Button>
                  </>
                ) : null}
              </div>
            </>
          )}
        </FloatingPanel>
      </FloatingPortal>
    ) : null;

  return { contentProps, card };
};
